import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Input } from "@/components/input";
import { LoadingSpinner } from "@/components/loading-spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip";
import { useExperimentTimeline } from "@/http/api-hooks";
import { ExperimentTimelineCampaign, ExperimentTimelineSegment } from "@/http/api-types";
import { cn, formatUTCDateTime } from "@/lib/utils";
import {
  type ExperimentTimelineDayMarker,
  buildExperimentTimelineMonthHeaders,
  buildExperimentTimelineScale,
  buildExperimentTimelineWeekTicks,
  filterExperimentTimelineCampaigns,
  formatExperimentTimelineDay,
  formatExperimentTimelineExclusiveEndDay,
  getExperimentTimelineInitialScrollDate,
  getExperimentTimelineLaneFrame,
  getExperimentTimelineScrollLeftForDate,
  getExperimentTimelineSegmentColor,
  getExperimentTimelineSegmentFrame,
  getExperimentTimelineUTCDayMarker,
} from "@/pages/experiments/experiment-timeline-utils";
import { RefreshCw, Search } from "lucide-react";
import { type Dispatch, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const LEFT_COLUMN_WIDTH = 340;
const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 72;
const TRACK_HEIGHT = 36;
const TRACK_TOP = 14;

type CampaignTypeFilter = "all" | "install" | "ctv_install";

export default function ExperimentsCampaignAssignmentPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, mutate } = useExperimentTimeline();
  const [campaignType, setCampaignType] = useState<CampaignTypeFilter>("all");
  const [search, setSearch] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const timelineData = data?.data;
  const scale = useMemo(() => {
    if (!timelineData) return null;
    return buildExperimentTimelineScale(timelineData.window_start, timelineData.window_end);
  }, [timelineData]);

  const visibleCampaigns = useMemo(
    () => filterExperimentTimelineCampaigns(timelineData?.campaigns || [], campaignType, search),
    [campaignType, search, timelineData?.campaigns],
  );

  const monthHeaders = useMemo(() => (scale ? buildExperimentTimelineMonthHeaders(scale) : []), [scale]);
  const weekTicks = useMemo(() => (scale ? buildExperimentTimelineWeekTicks(scale) : []), [scale]);
  const todayMarker = useMemo(() => (scale ? getExperimentTimelineUTCDayMarker(scale) : null), [scale]);

  useEffect(() => {
    if (!scale || !scrollContainerRef.current) {
      return;
    }

    scrollContainerRef.current.scrollLeft = getExperimentTimelineScrollLeftForDate(
      getExperimentTimelineInitialScrollDate(),
      scale,
    );
  }, [scale]);

  if (isLoading) {
    return (
      <div className="w-full flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !timelineData || !scale) {
    return <div className="text-sm text-destructive">Failed to load Experiment Timeline.</div>;
  }

  return (
    <TooltipProvider delayDuration={80}>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              value={campaignType}
              onValueChange={value => {
                if (value) setCampaignType(value as CampaignTypeFilter);
              }}
              className="w-full sm:w-auto"
            >
              <ToggleGroupItem value="all" className="min-w-20">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="install" className="min-w-24">
                Install
              </ToggleGroupItem>
              <ToggleGroupItem value="ctv_install" className="min-w-28">
                CTV Install
              </ToggleGroupItem>
            </ToggleGroup>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search campaigns"
                className="pl-8"
              />
            </div>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="outline" size="icon" onClick={() => mutate()}>
                <RefreshCw className="h-4 w-4" />
                <span className="sr-only">Refresh Experiment Timeline</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
        </div>

        <div className="rounded-md border bg-background">
          <div ref={scrollContainerRef} className="max-h-[calc(100vh-220px)] overflow-auto">
            <div
              className="relative grid"
              style={{
                gridTemplateColumns: `${LEFT_COLUMN_WIDTH}px ${scale.width}px`,
                minWidth: LEFT_COLUMN_WIDTH + scale.width,
              }}
            >
              {todayMarker && <ExperimentTimelineTodayLine marker={todayMarker} />}
              <div
                className="sticky left-0 top-0 z-40 border-b border-r bg-muted px-3 py-2"
                style={{ height: HEADER_HEIGHT }}
              >
                <div className="text-xs font-medium uppercase text-muted-foreground">Campaign</div>
                <div className="mt-2 text-sm font-medium">
                  {formatExperimentTimelineDay(timelineData.window_start)} -{" "}
                  {formatExperimentTimelineExclusiveEndDay(timelineData.window_end)}
                </div>
              </div>
              <div
                className="sticky top-0 z-30 border-b bg-muted"
                style={{ width: scale.width, height: HEADER_HEIGHT }}
              >
                <div className="relative h-full w-full">
                  {monthHeaders.map(month => (
                    <div
                      key={month.key}
                      className="absolute top-0 flex h-9 items-center border-r px-2 text-xs font-medium text-foreground"
                      style={{ left: month.left, width: month.width }}
                    >
                      {month.label}
                    </div>
                  ))}
                  {weekTicks.map(tick => (
                    <div
                      key={tick.key}
                      className="absolute bottom-0 h-9 border-l border-border/80 px-1 pt-2 text-[11px] text-muted-foreground"
                      style={{ left: tick.left }}
                    >
                      {tick.label}
                    </div>
                  ))}
                  {todayMarker && <ExperimentTimelineTodayHeaderMarker marker={todayMarker} />}
                </div>
              </div>

              {visibleCampaigns.length > 0 ? (
                visibleCampaigns.map(campaign => (
                  <ExperimentTimelineRow
                    key={campaign.campaign_id}
                    campaign={campaign}
                    monthHeaders={monthHeaders}
                    scale={scale}
                    onOpenExperiment={experimentId => navigate(`/experiments/${experimentId}`)}
                  />
                ))
              ) : (
                <>
                  <div
                    className="sticky left-0 z-20 border-r bg-background px-3 py-4 text-sm text-muted-foreground"
                    style={{ height: ROW_HEIGHT }}
                  >
                    No campaigns
                  </div>
                  <div className="relative border-t" style={{ width: scale.width, height: ROW_HEIGHT }}>
                    <ExperimentTimelineMonthLines monthHeaders={monthHeaders} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

function ExperimentTimelineTodayLine({ marker }: { marker: ExperimentTimelineDayMarker }) {
  return (
    <div
      className="pointer-events-none absolute bottom-0 top-0 z-10 w-0 border-l-2 border-sky-500/80"
      style={{ left: LEFT_COLUMN_WIDTH + marker.left }}
    />
  );
}

function ExperimentTimelineTodayHeaderMarker({ marker }: { marker: ExperimentTimelineDayMarker }) {
  return (
    <>
      <div
        className="pointer-events-none absolute bottom-0 top-0 z-20 w-0 border-l-2 border-sky-500"
        style={{ left: marker.left }}
      />
      <div
        className="pointer-events-none absolute bottom-1 z-30 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full bg-sky-600 text-[11px] font-semibold leading-none text-white shadow-sm ring-2 ring-background"
        style={{ left: marker.left }}
      >
        {marker.label}
      </div>
    </>
  );
}

function ExperimentTimelineRow({
  campaign,
  monthHeaders,
  scale,
  onOpenExperiment,
}: {
  campaign: ExperimentTimelineCampaign;
  monthHeaders: ReturnType<typeof buildExperimentTimelineMonthHeaders>;
  scale: ReturnType<typeof buildExperimentTimelineScale>;
  onOpenExperiment: Dispatch<number>;
}) {
  return (
    <>
      <div
        className="sticky left-0 z-20 flex min-w-0 items-center gap-2 border-t border-r bg-background px-3 shadow-[1px_0_0_0_hsl(var(--border))]"
        style={{ height: ROW_HEIGHT }}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {campaign.campaign_name || `Campaign ${campaign.campaign_id}`}
          </div>
          <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            <span>{campaign.campaign_id}</span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
            <span className="truncate">{campaign.audience_name || campaign.audience_id}</span>
          </div>
        </div>
        <Badge variant="outline" className="shrink-0 text-[10px]">
          {formatCampaignType(campaign.campaign_type)}
        </Badge>
      </div>
      <div className="relative border-t" style={{ width: scale.width, height: ROW_HEIGHT }}>
        <ExperimentTimelineMonthLines monthHeaders={monthHeaders} />
        {(campaign.segments ?? []).map((segment, index) => (
          <ExperimentTimelineSegmentBar
            key={`${campaign.campaign_id}-${segment.experiment_id}-${segment.start_at}-${segment.end_at}-${index}`}
            segment={segment}
            scale={scale}
            onOpenExperiment={onOpenExperiment}
          />
        ))}
      </div>
    </>
  );
}

function ExperimentTimelineMonthLines({
  monthHeaders,
}: {
  monthHeaders: ReturnType<typeof buildExperimentTimelineMonthHeaders>;
}) {
  return (
    <>
      {monthHeaders.slice(1).map(month => (
        <div
          key={month.key}
          className="pointer-events-none absolute bottom-0 top-0 w-px bg-border/80"
          style={{ left: month.left }}
        />
      ))}
    </>
  );
}

function ExperimentTimelineSegmentBar({
  segment,
  scale,
  onOpenExperiment,
}: {
  segment: ExperimentTimelineSegment;
  scale: ReturnType<typeof buildExperimentTimelineScale>;
  onOpenExperiment: Dispatch<number>;
}) {
  const frame = getExperimentTimelineSegmentFrame(segment, scale);
  const lane = getExperimentTimelineLaneFrame(segment.lane_index, segment.lane_count, TRACK_HEIGHT);
  const color = getExperimentTimelineSegmentColor(segment);
  const showLabel = frame.width >= 88 && lane.height >= 16;
  const tooltipStartAt = segment.experiment_start_at || segment.start_at;
  const tooltipEndAt = segment.experiment_end_at || segment.end_at;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "absolute flex items-center overflow-hidden rounded-[3px] border px-2 text-left text-[12px] font-medium leading-none shadow-sm ring-1 ring-background transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            segment.status === "concluded" ? "text-white/95" : "text-white",
          )}
          style={{
            left: frame.left,
            width: frame.width,
            top: TRACK_TOP + lane.top,
            height: lane.height,
            backgroundColor: color,
            borderColor: color,
          }}
          onClick={() => onOpenExperiment(segment.experiment_id)}
        >
          {showLabel && <span className="block truncate">{segment.experiment_name}</span>}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-sm">
        <div className="space-y-1">
          <div className="font-medium">{segment.experiment_name}</div>
          <div className="text-xs text-muted-foreground">
            {formatExperimentType(segment.experiment_type)} / {formatSegmentStatus(segment.status)}
          </div>
          <div className="text-xs">
            {formatUTCDateTime(tooltipStartAt)} - {formatUTCDateTime(tooltipEndAt)}
          </div>
          <div className="text-xs text-muted-foreground">{segment.membership_source}</div>
          {(segment.variant || segment.traffic_allocation) && (
            <div className="text-xs text-muted-foreground">
              {[
                segment.variant ? formatVariant(segment.variant) : null,
                formatTrafficAllocation(segment.traffic_allocation),
              ]
                .filter(Boolean)
                .join(" / ")}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function formatCampaignType(campaignType: string): string {
  if (campaignType === "ctv_install") return "CTV";
  return "Install";
}

function formatExperimentType(experimentType: string): string {
  if (experimentType === "custom") return "Custom";
  return "Global";
}

function formatSegmentStatus(status: string): string {
  switch (status) {
    case "active":
      return "Active";
    case "scheduled":
      return "Scheduled";
    case "waiting_for_outcome":
      return "Waiting";
    case "concluded":
      return "Concluded";
    default:
      return status;
  }
}

function formatVariant(variant: string): string {
  if (variant === "control") return "Control";
  if (variant === "test") return "Test";
  return variant;
}

function formatTrafficAllocation(value?: number): string | null {
  if (!value) return null;
  return `${(value / 100).toFixed(0)}%`;
}
